import { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { RemoteAppErrorBoundary } from '../RemoteAppErrorBoundary';
import { useModuleLoader } from '../../hooks/useModuleLoader';
import {
  useShellBridgeMount,
  type Auth0Ref,
  type PreBridgeMountArgs,
} from '../../hooks/useShellBridgeMount';
import { RemoteModuleWandLoader } from '../RemoteModuleWandLoader';
import { LoadingErrorPage } from '../../pages/LoadingErrorPage';
import type { AppRegistryCallbackContext } from '@opengov/psp-contract';
import type { ModuleRoute, RemoteModuleConfig } from '../../types';
import { auth0Config } from '../../utils';
import { detectPspHostingEnvironment } from '../../services/utils';
import { TestIds } from '../../constants';

/**
 * Stub auth ref for unauthenticated routes. The shell bridge reads through
 * this on every call; modules using `<ShellAuth0Provider mode="shell">` see
 * `isAuthenticated: false` and Promise rejections for token requests.
 *
 * Lives at module scope so the same reference is reused across renders.
 */
const UNAUTH_AUTH0_REF: Auth0Ref = {
  isAuthenticated: false,
  isLoading: false,
  user: undefined,
  error: undefined,
  // Reject on any call shape — the cast asserts type-compatibility with the
  // overloaded ref signature; the runtime impl ignores its args.
  getAccessTokenSilently: (() =>
    Promise.reject(
      new Error('getAccessTokenSilently is unavailable on unauthenticated routes')
    )) as Auth0Ref['getAccessTokenSilently'],
  getIdTokenClaims: () => Promise.resolve(undefined),
  logout: () => Promise.resolve(),
};

interface UnauthenticatedRemoteAppShellProps {
  basePath: string;
  /** Unresolved module route — resolver is called with minimal (no-auth) context */
  moduleRoute: ModuleRoute;
  moduleConfig?: (
    ctx: AppRegistryCallbackContext
  ) => Record<string, unknown> | Promise<Record<string, unknown>>;
}

/**
 * Simplified RemoteAppShell for unauthenticated routes.
 *
 * Key differences from RemoteAppShell:
 * - Does NOT call useEntity() or useAuth()
 * - Mounts module with activeEntity: undefined and no token
 * - Loading state is not gated on activeEntity or auth token
 */
export function UnauthenticatedRemoteAppShell({
  basePath,
  moduleRoute,
  moduleConfig,
}: UnauthenticatedRemoteAppShellProps) {
  const hostingEnvironment = detectPspHostingEnvironment();

  const [resolvedModuleUrl, setResolvedModuleUrl] = useState<string | null>(null);
  const [resolvedRegistryConfig, setResolvedRegistryConfig] = useState<Record<string, unknown>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const { module, loading, error, reload, retryAttempt, maxRetries } =
    useModuleLoader(resolvedModuleUrl);

  // Surface retry progress to the user during transient-failure recovery
  // (PSS-2536). The initial spinner stays unadorned; only retry phases get
  // the "Retrying… (attempt N of 3)" status line below the spinner.
  const retryMessage =
    retryAttempt > 0 ? `Retrying… (attempt ${retryAttempt} of ${maxRetries})` : undefined;

  // Build minimal context for resolver — no auth, no entity
  const minimalContext: AppRegistryCallbackContext = useMemo(
    () => ({
      hostingEnvironment,
      auth0Config,
      activeEntity: undefined,
      user: undefined,
    }),
    [hostingEnvironment]
  );

  // Resolve module URL from the unresolved route's resolver function
  useEffect(() => {
    let mounted = true;

    Promise.resolve(moduleRoute.resolver(minimalContext))
      .then((url) => {
        if (mounted && url !== resolvedModuleUrl) {
          console.log(`[UnauthenticatedRemoteAppShell] Resolved module URL: ${url}`);
          setResolvedModuleUrl(url);
        }
      })
      .catch((err) => {
        console.error('[UnauthenticatedRemoteAppShell] Failed to resolve module URL:', err);
        if (mounted) setResolvedModuleUrl(null);
      });

    return () => {
      mounted = false;
    };
  }, [moduleRoute, minimalContext, resolvedModuleUrl]);

  // Resolve moduleConfig if provided
  useEffect(() => {
    if (!moduleConfig) return;
    let mounted = true;

    Promise.resolve(moduleConfig(minimalContext))
      .then((config) => {
        if (mounted) setResolvedRegistryConfig(config);
      })
      .catch((err) => {
        console.error('[UnauthenticatedRemoteAppShell] Failed to resolve moduleConfig:', err);
      });

    return () => {
      mounted = false;
    };
  }, [moduleConfig, minimalContext]);

  // Build mount options — no auth token, no entity
  const moduleMountArgs: PreBridgeMountArgs = useMemo(
    () => ({
      baseRouterPath: basePath,
      auth0Config, // no token
      activeEntity: undefined,
      hostingEnvironment,
      registryConfig: resolvedRegistryConfig,
    }),
    [basePath, hostingEnvironment, resolvedRegistryConfig]
  );

  // Store latest mount args in ref to avoid triggering re-mounts
  const moduleMountArgsRef = useRef(moduleMountArgs);
  const resolvedModuleUrlRef = useRef(resolvedModuleUrl);
  const showDebugInfoRef = useRef(false);
  const auth0Ref = useRef<Auth0Ref | null>(UNAUTH_AUTH0_REF);
  useEffect(() => {
    moduleMountArgsRef.current = moduleMountArgs;
    resolvedModuleUrlRef.current = resolvedModuleUrl;
  });

  // Construct an appConfig from the unauth shell's props. useShellBridgeMount
  // only reads `id` (for bridge creation and logging) and `basePath` (for debug
  // logs); the resolver-shape mismatch with `moduleRoutes` is sidestepped with
  // an empty array — the hook never iterates over routes.
  const appConfig: RemoteModuleConfig = useMemo(
    () => ({
      id: `unauth-${basePath}`,
      basePath,
      moduleRoutes: [],
    }),
    [basePath]
  );

  // Mount/unmount lifecycle — only depends on module (no activeEntity dependency).
  // Switched from useRemoteModuleMount to useShellBridgeMount so unauthenticated
  // modules also receive a shell bridge (with the stub auth provider above), so
  // modules using `<ShellAuth0Provider mode="shell">` work uniformly across
  // authenticated and unauthenticated routes.
  useShellBridgeMount({
    module,
    containerRef,
    mountArgsRef: moduleMountArgsRef,
    auth0Ref,
    remountDeps: [basePath],
    appConfig,
    hostingEnvironment,
    showDebugInfoRef,
    resolvedModuleUrlRef,
  });

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <RemoteAppErrorBoundary fallbackHeight="100%">
        {loading && (
          <Box data-testid={TestIds.UNAUTH_LOADING_SPINNER} sx={{ width: '100%', height: '100%' }}>
            <RemoteModuleWandLoader message={retryMessage} />
          </Box>
        )}

        {error && (
          <Box data-testid={TestIds.UNAUTH_ERROR_PAGE} sx={{ width: '100%', height: '100%' }}>
            <LoadingErrorPage error={error} onRetry={reload} />
          </Box>
        )}

        <Box
          data-testid={TestIds.UNAUTH_REMOTE_APP_CONTAINER}
          id="psp-unauth-remote-app-shell-container"
          ref={containerRef}
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'auto',
            display: loading || error ? 'none' : 'flex',
            flexDirection: 'column',
          }}
        />
      </RemoteAppErrorBoundary>
    </Box>
  );
}
